/**
 * Shared parsing/formatting helpers for the per-entity-type CRM trigger
 * card variants (meeting notes, meeting, research, generic, email thread).
 */

import type { CrmEventTriggerData } from '@/lib/proposals/core/types';
import { formatTime, type TimeFormat } from '@/lib/time';
import { formatCurrency } from '@/utils/currency';

/**
 * Extract a string value from the event data.
 * Returns null if the key is missing or the value is not a string.
 */
export function getStringValue(
  event: Record<string, unknown>,
  key: string
): string | null {
  const value = event[key];
  return typeof value === 'string' ? value : null;
}

/**
 * Extract a string array value from the event data.
 * Returns empty array if the key is missing or the value is not an array.
 */
export function getStringArrayValue(
  event: Record<string, unknown>,
  key: string
): string[] {
  const value = event[key];
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string');
  }
  return [];
}

/**
 * Parse action items from a multiline string
 */
export function parseActionItems(text: string | null): string[] {
  if (!text) {
    return [];
  }
  return text
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0)
    .map(line => {
      // Remove leading dash, asterisk, or bullet
      if (
        line.startsWith('- ') ||
        line.startsWith('* ') ||
        line.startsWith('• ')
      ) {
        return line.slice(2).trim();
      }
      // Remove numbered list prefix
      const numberedMatch = line.match(/^\d+\.\s+(.*)$/);
      if (numberedMatch) {
        return numberedMatch[1].trim();
      }
      return line;
    });
}

/**
 * Format meeting time as relative string (e.g., "Yesterday at 2:00 PM")
 */
export function formatMeetingTime(
  isoString: string,
  timeFormat: TimeFormat
): string {
  const date = new Date(isoString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const meetingDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  const diffDays = Math.round(
    (today.getTime() - meetingDay.getTime()) / (1000 * 60 * 60 * 24)
  );

  // Route the time portion through the shared formatter so it inherits the
  // org format handling and the midnight-safe hour cycle.
  const timeStr = formatTime(date, timeFormat);

  if (diffDays === 0) {
    return `Today at ${timeStr}`;
  } else if (diffDays === 1) {
    return `Yesterday at ${timeStr}`;
  } else if (diffDays < 7) {
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
    return `${dayName} at ${timeStr}`;
  } else {
    const dateStr = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    return `${dateStr} at ${timeStr}`;
  }
}

/**
 * Parse key topics from comma-separated or newline-separated string
 */
export function parseKeyTopics(text: string | null): string[] {
  if (!text) {
    return [];
  }
  // Try comma-separated first
  if (text.includes(',')) {
    return text
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0);
  }
  // Try newline-separated
  return text
    .split('\n')
    .map(t => t.trim())
    .filter(t => t.length > 0);
}

const GENERIC_PREVIEW_FALLBACK_CURRENCY = 'USD';

/**
 * Format a numeric/QuantityValue amount for the generic preview line.
 * Handles bare numbers and the `{ amount, unit }` shape used by currency
 * properties like `ahoy_amount`. Returns null when the value can't be
 * rendered as a number.
 */
function formatPreviewAmount(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return formatCurrency(value, GENERIC_PREVIEW_FALLBACK_CURRENCY);
  }
  if (value !== null && typeof value === 'object' && 'amount' in value) {
    const { amount, unit } = value as {
      amount: string | number | null | undefined;
      unit?: string;
    };
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (typeof num === 'number' && Number.isFinite(num)) {
      return formatCurrency(num, unit || GENERIC_PREVIEW_FALLBACK_CURRENCY);
    }
  }
  return null;
}

/**
 * Pull the most useful "secondary" fields out of a generic CRM trigger
 * for the collapsed preview line. Returns up to two short strings that
 * are joined with a middot in the preview body.
 *
 * Keyed by entity_type_name so the preview line stays meaningful per
 * type (contact → email + role, company → domain + industry, etc.).
 * Returns an empty array for unknown types — the card stays header-only.
 */
export function getGenericPreviewFields(data: CrmEventTriggerData): string[] {
  const { entity_type_name, event } = data.crm_event;
  const out: string[] = [];

  const push = (value: unknown) => {
    if (typeof value === 'string' && value.trim().length > 0) {
      out.push(value.trim());
    } else if (Array.isArray(value)) {
      const first = value.find(
        (v): v is string => typeof v === 'string' && v.trim().length > 0
      );
      if (first) {
        out.push(first.trim());
      }
    } else {
      const amount = formatPreviewAmount(value);
      if (amount) {
        out.push(amount);
      }
    }
  };

  if (entity_type_name === 'ahoy_contact') {
    push(event['ahoy_job_title']);
    push(event['ahoy_email']);
  } else if (entity_type_name === 'ahoy_company') {
    push(event['ahoy_domain']);
    push(event['ahoy_industry']);
  } else if (entity_type_name === 'ahoy_deal') {
    push(event['ahoy_stage']);
    push(event['ahoy_amount']);
  }

  return out.slice(0, 2);
}
